import { enqueueSnackbar } from 'notistack';
import { useState, ReactNode, useCallback } from 'react';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { ListEntityType } from 'src/services/lists/lists.types';
import { SignerInput, SigningOriginType } from 'src/services/signing/signing.types';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';
import { CallListAddedFrom, CallListPersonType } from 'src/services/call-lists/call-lists.types';
import { isShadowEmail, exportOrgCandidateProfile } from 'src/services/candidates/candidates.utils';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
import { openMultiMailerDialog, openSingleMailerDialog, openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { useGetCandidateDocumentsQuery, useGetOrganizationCandidateQuery } from 'src/services/candidates/candidates.service';

import { RecordAction } from 'src/components/record-panel/record-panel.types';

import AddItemsModal from 'src/sections/lists/modals/add-items-modal';
import DoNotCallDialog from 'src/sections/call-lists/modals/do-not-call-dialog';
import AddCandidateJobModal from 'src/sections/jobs/modals/add-candidate-job-modal';
import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';
import AddToCallListModal from 'src/sections/call-lists/modals/add-to-call-list-modal';
import { useCallListsEnabled } from 'src/sections/call-lists/hooks/use-call-lists-enabled';
import BulkAddCandidatesJobModal from 'src/sections/jobs/modals/bulk-add-candidate-job-modal';
import BulkPresentationsDialog from 'src/sections/presentations/modals/bulk-presentations-dialog';
import CreatePresentationDialog from 'src/sections/presentations/modals/create-presentation-dialog';
import OrgCandidateProfileImporterDialog from 'src/sections/candidates/modal/candidate-profile-importer';
import SendForSigningModal, { SendForSigningDocument } from 'src/sections/signing/send-for-signing-modal';
import ScheduleMeetingModal, { ScheduleMeetingInitialAttendee } from 'src/sections/calendar/modal/schedule-meeting-modal';
import { buildDialNumber, useCommunicationActions } from 'src/sections/admin/integrations/communications/use-communication-actions';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

// ----------------------------------------------------------------------

export type CandidateActionKey =
  | 'send-signing'
  | 'copy-email'
  | 'send-email'
  | 'schedule-meeting'
  | 'call'
  | 'send-sms'
  | 'copy-phone'
  | 'import-profile'
  | 'export-profile'
  | 'create-presentation'
  | 'create-presentations'
  | 'add-to-job'
  | 'add-to-list'
  | 'add-to-call-list'
  | 'do-not-call'
  | 'add-task';

/** What the actions need to know about one candidate. `id` is the OrganizationCandidate id. */
export interface CandidateActionTarget {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  countryCode?: string | null;
  doNotCall?: boolean | null;
}

interface Options {
  /**
   * `record`: one candidate's own page or panel. Nothing is offered without an id, and actions that
   * don't apply to the record (no email, no phone) are hidden.
   *
   * `selection`: a list toolbar acting on the ticked rows. Add to Job, Add to list and Send Email take
   * every ticked row; the rest are disabled unless exactly one row is ticked. Disabled, not hidden,
   * so the menu doesn't change shape as rows are ticked.
   */
  mode: 'record' | 'selection';
  /** OrganizationCandidate ids. */
  ids: string[];
  /** The candidate's details when the caller already has them; skips the record fetch. */
  target?: CandidateActionTarget;
  exclude?: CandidateActionKey[];
  /** Preselects this job in Add to job — set when the profile is opened from a job's Sourcing tab. */
  jobId?: string;
  onAddedToList?: () => void;
}

type OpenModal = 'signing' | 'meeting' | 'import' | 'add-to-job' | 'add-to-list' | 'add-to-call-list' | 'do-not-call' | 'create-presentation' | 'create-presentations';

/**
 * The candidate actions, in one list, for every place a candidate can be acted on: the profile page,
 * the quick view, the list toolbar and the application profile (JT1-1799). Each place used to build
 * its own list, and they drifted apart.
 *
 * Render `modals` exactly once, at the caller's root, never inside a popover: the menu closes before
 * its action runs, and a modal inside it would unmount with it. Each modal mounts only while open, so
 * the job and list pickers don't fetch in the background and the signing and meeting dialogs, which
 * read their props once at mount, always open with current data.
 */
export function useCandidateActions({ mode, ids, target, exclude, jobId, onAddedToList }: Options): {
  actions: RecordAction[];
  modals: ReactNode;
} {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const tenantType = useOrgTenant();
  const { featureEnabled } = useFeatureFlags();
  const canUseCallLists = useCallListsEnabled();

  const [openModal, setOpenModal] = useState<OpenModal | null>(null);
  const closeModal = useCallback(() => setOpenModal(null), []);

  const isCandidateTenant = tenantType === TenantType.Candidate;
  const single = ids.length === 1 ? ids[0] : undefined;

  // A cache hit on the profile page and the quick view, which already load the record. Candidate
  // tenants have no CRM record to load.
  const { currentData: fetched } = useGetOrganizationCandidateQuery(single as string, {
    skip: !single || !!target || isCandidateTenant,
  });

  // `currentData` from the previous record survives a prev/next step in the quick view until the
  // next one loads; an id mismatch is treated as still loading so nothing acts on the wrong person.
  let record: CandidateActionTarget | undefined;
  if (single && target?.id === single) {
    record = target;
  } else if (single && !target && fetched?.id === single) {
    record = {
      id: fetched.id,
      firstName: fetched.candidate_profile?.first_name,
      lastName: fetched.candidate_profile?.last_name,
      email: fetched.email,
      phone: fetched.privileged_access ? fetched.phone_number : null,
      countryCode: fetched.country_code,
      doNotCall: fetched.do_not_call,
    };
  }

  const name = `${record?.firstName ?? ''} ${record?.lastName ?? ''}`.trim();
  const email = isShadowEmail(record?.email) ? undefined : (record?.email as string);
  const dialNumber = buildDialNumber(record?.phone, record?.countryCode);

  const comms = useCommunicationActions(
    dialNumber,
    name,
    single ? { entity_type: CommunicationEntityType.CANDIDATE, entity_id: single } : undefined
  );

  // Signing needs the candidate's documents, loaded only once Send for Signing is clicked. The
  // dialog waits for them because it picks its starting tab from them at mount. Same arguments as
  // the add-to-job modal, so the two share the cache.
  const signingDocsQuery = useGetCandidateDocumentsQuery(
    { candidate_id: single as string, params: { page: 1, per_page: 50 } },
    { skip: !single || openModal !== 'signing' }
  );
  const signingDocsReady = !signingDocsQuery.isUninitialized && !signingDocsQuery.isFetching;

  const copy = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
  }, [t]);

  if (mode === 'record' && (!single || isCandidateTenant)) {
    return { actions: [], modals: null };
  }

  const selectOne = t('record_actions.select_one_candidate', { defaultValue: 'Select one candidate' });
  const noEmail = t('calendar.schedule_meeting.no_email');
  const noPhone = t('record_actions.no_phone', { defaultValue: 'No phone number on this record' });

  /** Gating for an action that acts on one candidate and may need a detail the record lacks. */
  const forOne = (applies: boolean, missing?: string): Pick<RecordAction, 'hidden' | 'disabled' | 'caption'> => {
    if (mode === 'record') return { hidden: !applies };
    if (!record) return { disabled: true, caption: selectOne };
    return applies ? {} : { disabled: true, caption: missing };
  };

  const canEmail = featureEnabled(FeatureType.EMAIL_CONNECTOR_ENABLED) && !isCandidateTenant;

  const handleSendEmail = () => {
    if (mode === 'selection') {
      dispatch(openMultiMailerDialog({
        targetType: MailerTargetType.CANDIDATE,
        templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
        entities: ids,
      }));
      return;
    }
    if (!email || !single) return;
    dispatch(openSingleMailerDialog({
      emails: [email],
      templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      templateEntityId: single,
      targetType: MailerTargetType.CANDIDATE,
      targetId: single,
    }));
  };

  const actions: RecordAction[] = [
    {
      key: 'send-signing',
      label: t('common.send_for_signing', { defaultValue: 'Send for Signing' }),
      icon: 'mdi:signature-freehand',
      onClick: () => setOpenModal('signing'),
      ...forOne(true),
    },
    {
      key: 'copy-email',
      label: t('candidates.detail.actions.copy_email'),
      icon: 'mdi:email-outline',
      onClick: () => email && copy(email),
      ...forOne(!!email, noEmail),
    },
    {
      key: 'send-email',
      label: t('common.send_email'),
      icon: 'lsicon:email-send-filled',
      onClick: handleSendEmail,
      ...(mode === 'selection'
        ? { hidden: !canEmail, disabled: !ids.length }
        : { hidden: !canEmail || !email }),
    },
    {
      key: 'schedule-meeting',
      label: t('calendar.schedule_meeting.action'),
      icon: 'mdi:calendar-plus',
      onClick: () => setOpenModal('meeting'),
      // Kept visible on a record without an email, with the reason, as the profile always showed it.
      ...(mode === 'record'
        ? { disabled: !email, caption: email ? undefined : noEmail }
        : forOne(!!email, noEmail)),
    },
    {
      key: 'call',
      label: t('integrations.communications.actions.call'),
      icon: 'solar:phone-bold',
      onClick: () => comms.call(),
      // JT1-1827: nobody calls someone marked do not call
      ...(comms.available && !record?.doNotCall ? forOne(!!dialNumber, noPhone) : { hidden: true }),
      ...(comms.calling && { disabled: true }),
    },
    {
      key: 'send-sms',
      label: t('integrations.communications.actions.sms'),
      icon: 'solar:chat-round-dots-bold',
      onClick: () => comms.openSms(),
      ...(comms.available ? forOne(!!dialNumber, noPhone) : { hidden: true }),
    },
    {
      key: 'copy-phone',
      label: t('candidates.detail.actions.copy_phone'),
      icon: 'solar:phone-linear',
      onClick: () => dialNumber && copy(dialNumber),
      ...forOne(!!dialNumber, noPhone),
    },
    {
      key: 'import-profile',
      label: t('candidates.detail.actions.import_profile'),
      icon: 'bx:import',
      onClick: () => setOpenModal('import'),
      ...forOne(true),
    },
    {
      key: 'export-profile',
      label: t('candidates.detail.actions.export_profile'),
      icon: 'bx:export',
      onClick: () => single && exportOrgCandidateProfile(single),
      ...forOne(true),
    },
    {
      key: 'create-presentation',
      label: t('presentations.actions.create'),
      icon: 'solar:document-add-bold',
      onClick: () => setOpenModal('create-presentation'),
      // JT1-1886: agencies with the presentation builder; recruiters only.
      ...(featureEnabled(FeatureType.CANDIDATE_PRESENTATIONS_ENABLED) && tenantType === TenantType.Recruiter ? forOne(true) : { hidden: true }),
    },
    {
      key: 'create-presentations',
      label: t('presentations.bulk.action'),
      icon: 'solar:documents-bold',
      onClick: () => setOpenModal('create-presentations'),
      // JT1-1887: one file each for every ticked row, made in the background; list toolbars only.
      ...(featureEnabled(FeatureType.CANDIDATE_PRESENTATIONS_ENABLED) && tenantType === TenantType.Recruiter && mode === 'selection' ? { disabled: !ids.length } : { hidden: true }),
    },
    {
      key: 'add-to-job',
      label: t('common.add_to_job'),
      icon: 'mdi:invite',
      onClick: () => setOpenModal('add-to-job'),
      disabled: !ids.length,
    },
    {
      key: 'add-to-list',
      label: t('common.add_to_list', { defaultValue: 'Add to list' }),
      icon: 'solar:list-bold',
      onClick: () => setOpenModal('add-to-list'),
      disabled: !ids.length,
    },
    {
      key: 'add-to-call-list',
      label: t('call_lists.add_to_call_list'),
      icon: 'solar:phone-calling-rounded-bold',
      onClick: () => setOpenModal('add-to-call-list'),
      hidden: !canUseCallLists,
      ...(mode === 'record' && record?.doNotCall
        ? { disabled: true, caption: t('call_lists.do_not_call') }
        : { disabled: !ids.length }),
    },
    {
      key: 'do-not-call',
      label: record?.doNotCall ? t('call_lists.dnc.clear') : t('call_lists.dnc.mark'),
      icon: record?.doNotCall ? 'solar:phone-calling-rounded-bold' : 'solar:forbidden-circle-bold',
      onClick: () => setOpenModal('do-not-call'),
      ...(mode === 'record' && !isCandidateTenant ? forOne(true) : { hidden: true }),
    },
    {
      key: 'add-task',
      label: t('common.add_task'),
      icon: 'ic:round-add-task',
      onClick: () => single && dispatch(openTaskEditorSidesheet({ candidateId: single })),
      ...forOne(true),
    },
  ];

  const signingDocuments: SendForSigningDocument[] = (signingDocsQuery.currentData?.results ?? []).map((d) => ({
    file_id: d.document?.file_id ?? d.document?.id ?? d.id,
    name: d.document?.name ?? d.document?.filename ?? 'Document',
    mime: d.document?.mime,
  }));

  const signers: SignerInput[] | undefined = email
    ? [{ first_name: record?.firstName ?? '', last_name: record?.lastName ?? '', email }]
    : undefined;

  // The schedule endpoint expects the OrganizationCandidate id, not the CandidateProfile id.
  const attendee: ScheduleMeetingInitialAttendee | undefined = single
    ? { type: 'candidate', id: single, label: name, email }
    : undefined;

  const modals = (
    <>
      {openModal === 'signing' && single && signingDocsReady && (
        <SendForSigningModal
          open
          onClose={closeModal}
          originType={SigningOriginType.CANDIDATE}
          originId={single}
          documents={signingDocuments}
          defaultSigners={signers}
        />
      )}

      {openModal === 'meeting' && attendee && (
        <ScheduleMeetingModal open onClose={closeModal} initialAttendee={attendee} />
      )}

      {openModal === 'import' && single && (
        <OrgCandidateProfileImporterDialog candidateId={single} open onClose={closeModal} onSuccess={closeModal} />
      )}

      {openModal === 'add-to-job' && (mode === 'record' && single ? (
        <AddCandidateJobModal open candidateId={single} jobId={jobId} onClose={closeModal} />
      ) : (
        <BulkAddCandidatesJobModal open candidates={ids} onClose={closeModal} />
      ))}

      {openModal === 'add-to-list' && (
        <AddItemsModal
          open
          entityType={ListEntityType.CANDIDATE}
          items={ids}
          onClose={closeModal}
          onSuccess={() => {
            closeModal();
            onAddedToList?.();
          }}
        />
      )}

      {openModal === 'add-to-call-list' && (
        <AddToCallListModal
          open
          onClose={closeModal}
          personType={CallListPersonType.CANDIDATE}
          ids={ids}
          personName={name || undefined}
          addedFrom={mode === 'record' ? CallListAddedFrom.CANDIDATE_PROFILE : CallListAddedFrom.CANDIDATE_LIST}
        />
      )}

      {openModal === 'do-not-call' && single && (
        <DoNotCallDialog
          open
          onClose={closeModal}
          personType={CallListPersonType.CANDIDATE}
          personId={single}
          personName={name || undefined}
          doNotCall={!record?.doNotCall}
        />
      )}

      {openModal === 'create-presentation' && single && (
        <CreatePresentationDialog open onClose={closeModal} candidateId={single} candidateName={name || undefined} />
      )}

      {openModal === 'create-presentations' && ids.length > 0 && (
        <BulkPresentationsDialog open onClose={closeModal} candidateIds={ids} />
      )}

      {comms.smsModal}
    </>
  );

  return {
    actions: exclude?.length ? actions.filter((action) => !exclude.includes(action.key as CandidateActionKey)) : actions,
    modals,
  };
}
